import { useTranslation } from 'react-i18next';

// Utils & misc
import cn from '../../../utils/cn';

// Components
import FavoriteToggleButton from '../../atoms/FavoriteToggleButton/FavoriteToggleButton';

// Style
import style from './NavigationLinkGroup.module.css';

/**
 * @typedef {object} NavigationLink
 * @property {string} id
 * @property {string} label
 * @property {string} href
 * @property {boolean} [isFavorite] - Filled star, always visible
 */

/**
 * NavigationLinkGroup component - Optional title followed by a vertical list of links.
 * Plain clicks are delegated to `onLinkClick` (client navigation); modifier clicks keep the native behavior.
 * @param {object} params
 * @param {string | null} [params.title=null] - Group title
 * @param {NavigationLink[]} params.links - Links of the group
 * @param {(id: string) => void} params.onLinkClick - Click handler
 * @param {number} [params.columns=1] - Number of columns the links are spread over
 * @param {string} [params.className] - Additional CSS classes
 * @param {(id: string) => void} [params.onFavoriteToggle] - Star click handler (no star when omitted)
 * @returns {JSX.Element} NavigationLinkGroup component
 */
function NavigationLinkGroup({
  title = null,
  links,
  onLinkClick,
  columns = 1,
  className,
  onFavoriteToggle,
}) {
  const { t } = useTranslation();

  return (
    <div className={cn([style['link-group'], className])}>
      {title ? <p className={style.title}>{title}</p> : null}
      <ul
        className={style.list}
        style={/** @type {React.CSSProperties & { '--link-columns': number }} */ ({ '--link-columns': columns })}
      >
        {links.map((link) => (
          <li key={link.id} className={style.item}>
            <a
              href={link.href}
              className={cn([style.link, onFavoriteToggle && style['link-with-favorite']])}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                e.preventDefault();
                onLinkClick(link.id);
              }}
            >
              {link.label}
            </a>
            {onFavoriteToggle ? (
              <FavoriteToggleButton
                isFavorite={!!link.isFavorite}
                label={t(link.isFavorite ? 'menu.management.removeFromFavorites' : 'menu.management.addToFavorites')}
                className={cn([style.favorite, link.isFavorite && style['favorite-on']])}
                onToggle={() => onFavoriteToggle(link.id)}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default NavigationLinkGroup;
